import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Col, useDeviceType } from '@cian/ui-kit';

import { ListingHeaderContainer } from '../ListingHeader';
import { PaginationContainer } from '../Pagination';
import {
  prepareListingOrdering,
  preparePageNumber,
  useGetListing,
  useListingAnalytics,
  useTimeoutEffect,
  buildPostUrl,
} from '../../utils';
import { ARTICLE_LIST_REQUEST_LIMIT } from '../../constants';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { selectListingAppendType } from '../../selectors/settings/selectListingAppendType';
import { getArticleList, resetArticleList } from '../../actions/articleList';
import { selectCurrentPageMeta } from '../../selectors/pagesMeta';
import { EType } from '../../repositories/journal/entities/page_info/PageInfoSchema';
import {
  CardListingItem,
  ListingItem,
  SkeletonCardListingItem,
  SkeletonListingItem,
} from '../../components/ListingItem';
import { selectArticleList, selectArticleListStatus } from '../../selectors/articleList';
import { ERequestStatus } from '../../types/requestStatus';
import { selectFavoriteListingType } from '../../selectors/settings/selectFavoriteListingType';
import { EListingTypes } from '../../types/listing';
import { EType as EPostType } from '../../repositories/journal/entities/journal/JournalListAttributesSchema';
import * as s from './Listings.css';

/**
 * Листинг статей
 */
export const ArticleList = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const qsPage = preparePageNumber(searchParams.get('page') as string);
  const qsWithTag = searchParams.get('with-tag') || '';
  const qsOrdering = prepareListingOrdering(searchParams.get('ordering') || '') || '';
  const appendType = useSelector(selectListingAppendType);
  const favoriteListingType = useSelector(selectFavoriteListingType);
  const {
    type: pageType,
    pathname: currentPageTypePathname,
    rubricId,
  } = useSelector((state: IApplicationState) => selectCurrentPageMeta(state, pathname));
  const deviceType = useDeviceType();
  const [IsShownAsList, setIsShownAsList] = useState(false);
  const isPhone = () => deviceType === 'phone';
  const articles = useSelector(selectArticleList);
  const articlesStatus = useSelector(selectArticleListStatus);
  const [isSkeletonShown, setIsSkeletonShown] = useState(
    articlesStatus === ERequestStatus.Loading && appendType === 'set',
  );
  const { title } = useSelector((state: IApplicationState) => selectCurrentPageMeta(state, pathname));

  /** Если не нашел нужный тип, выкидывает на дефолтный */
  useEffect(() => {
    if (![EType.Articles, EType.Documents].includes(pageType)) {
      history.replace(currentPageTypePathname);
    }
  }, [currentPageTypePathname, history, pageType]);

  /** Трекает пагинацию */
  useListingAnalytics({
    page: {
      extra: {
        journalPageType: 'Rubric',
        journalRubrics: title,
      },
    },
  });

  /** Забирает статьи в стор */
  useGetListing(() => {
    dispatch(
      getArticleList({
        tag: qsWithTag,
        offset: (qsPage - 1) * ARTICLE_LIST_REQUEST_LIMIT,
        limit: ARTICLE_LIST_REQUEST_LIMIT,
        setType: appendType,
        rubricId: rubricId || undefined,
        ordering: qsOrdering || undefined,
      }),
    ).finally();
  });

  /** При выходе сбрасывает стор статей */
  useEffect(() => {
    return () => {
      dispatch(resetArticleList());
    };
  }, [dispatch]);

  /** Устанавливает вид листинга: карточки или список */
  useEffect(() => {
    setIsShownAsList(favoriteListingType === EListingTypes.List && deviceType !== 'phone');
  }, [favoriteListingType, deviceType]);

  /** Создает небольшую задержку перед скрытием скелетона, когда элементов не найдено */
  useTimeoutEffect(() => {
    setIsSkeletonShown(articlesStatus === ERequestStatus.Loading && appendType === 'set');
  }, [!isSkeletonShown || articles.length ? 0 : 500]);

  /**
   * При клике по плашке статьи роутит на соответствующую страницу статьи
   * При клике по шильдику рубрики в плашке статьи роутит на соответствующую рубрику
   */
  const handleClick = useCallback(
    (e, url: string) => {
      e.preventDefault();
      history.push(url);
    },
    [history],
  );

  return (
    <>
      <div className={s['listing-header-wrapper']}>
        <ListingHeaderContainer />
      </div>

      <div className={IsShownAsList ? s['list-wrapper'] : s['cards-wrapper']}>
        {isSkeletonShown &&
          [...Array(6)].map((_, index) =>
            IsShownAsList ? (
              <div key={index} className={s['list-item-wrapper']}>
                <SkeletonListingItem />
              </div>
            ) : (
              <Col key={index} xs={12} m={6} l={4}>
                <SkeletonCardListingItem />
              </Col>
            ),
          )}

        {((articlesStatus === ERequestStatus.Succeed && appendType === 'set') || appendType === 'append') &&
          articles.map(
            (
              { id, attributes: { image, title, subtitle, datePublish, numViews, commentsCount, rubrics, slug } },
              index,
            ) =>
              IsShownAsList ? (
                <div key={index} className={s['list-item-wrapper']}>
                  <ListingItem
                    title={title}
                    subtitle={subtitle}
                    image={image}
                    rubric={rubrics?.length ? rubrics[0] : undefined}
                    datePublish={datePublish}
                    numViews={numViews}
                    numComments={commentsCount}
                    url={buildPostUrl({ type: EPostType.Articles, slug, id })}
                    onRubricButtonClick={handleClick}
                    onClick={e => handleClick(e, buildPostUrl({ type: EPostType.Articles, slug, id }))}
                  />
                </div>
              ) : (
                <Col key={index} xs={12} m={6} l={4}>
                  <CardListingItem
                    title={title}
                    subtitle={image ? undefined : subtitle}
                    height={isPhone() ? undefined : 400}
                    image={image}
                    rubric={rubrics?.length ? rubrics[0] : undefined}
                    datePublish={datePublish}
                    numViews={numViews}
                    numComments={commentsCount}
                    url={buildPostUrl({ type: EPostType.Articles, slug, id })}
                    onRubricButtonClick={handleClick}
                    onClick={e => handleClick(e, buildPostUrl({ type: EPostType.Articles, slug, id }))}
                  />
                </Col>
              ),
          )}

        {!articles.length && (
          <div className={s['nothing-found']}>
            Увы... Ничего не нашлось.{' '}
            {(qsPage > 1 || qsWithTag) && (
              <>
                Попробуйте{' '}
                <a href={currentPageTypePathname} onClick={e => handleClick(e, currentPageTypePathname)}>
                  сбросить фильтры
                </a>
              </>
            )}
          </div>
        )}
      </div>

      {articlesStatus === ERequestStatus.Succeed && (
        <div className={s['controls-wrapper']}>
          <PaginationContainer />
        </div>
      )}
    </>
  );
};
